import { useFormContext } from "react-hook-form";
import { components, ControlProps } from "react-select";
import { styled } from "~/application/theme";

const StyledControl = styled(components.Control, {
  // Reset
  boxSizing: "border-box",
  display: "flex",
  userSelect: "none",

  // Custom
  position: "relative",
  transition: "$fast",
  outline: "none",
  gap: "$2",
  fontSize: "$md",
  fontWeight: 500,
  flexWrap: "wrap",
  width: "100%",
  minWidth: "150px",
});

const SelectControlRoot = styled("div", {
  // Reset
  boxSizing: "border-box",
  display: "flex",
  userSelect: "none",

  // Custom
  position: "relative",
  transition: "$fast",
  outline: "none",
  gap: "$2",
  fontSize: "$md",
  fontWeight: 500,
  flexWrap: "wrap",
  border: "none",
  borderRadius: "$md",
  backgroundColor: "$neutrals-white",
  color: "$neutral-500",
  fill: "$neutral-500",

  "&[data-transparent-bg=true]": {
    backgroundColor: "transparent",
  },

  "&::after": {
    content: "",
    pointerEvents: "none",
    position: "absolute",
    margin: 0,
    inset: 0,
    borderColor: "$neutral-300",
    borderRadius: "inherit",
    borderStyle: "solid",
    borderWidth: "1px",
    transition: "$faster",
  },

  "&:hover": {
    cursor: "pointer",
  },

  "&[data-dirty=true]": {
    backgroundColor: "$error-light",
    "&::after": {
      borderWidth: "2px",
      borderColor: "$danger-500",
    },
  },

  "&[data-focused=true]": {
    boxShadow: "$focus-sm",
  },

  "&[data-filled=true]": {
    "&::after": {
      borderWidth: "2px",
      borderColor: "$primary-600",
    },
  },

  "&[data-disabled=true]": {
    pointerEvents: "none",
    backgroundColor: "$neutral-100",
    color: "$neutral-300",
    cursor: "not-allowed",

    "&::after": {
      borderWidth: "1px",
      borderColor: "$neutral-300",
    },

    svg: {
      fill: "$neutral-300",
    },
  },

  variants: {
    variant: {
      primary: {
        fill: "$primary-600",
        "&[data-filled=true]": {
          "&::after": {
            borderWidth: "2px",
            borderColor: "$primary-600",
          },
        },
        "&[data-disabled=true]": {
          backgroundColor: "$primary-50",
          "&::after": {
            borderWidth: "1px",
            borderColor: "$primary-300",
          },
          svg: {
            fill: "$primary-300",
          },
        },
      },
      success: {
        fill: "$success-600",
        "&[data-filled=true]": {
          "&::after": {
            borderWidth: "2px",
            borderColor: "$success-600",
          },
        },
        "&[data-disabled=true]": {
          backgroundColor: "$success-50",
          "&::after": {
            borderWidth: "1px",
            borderColor: "$success-300",
          },
          svg: {
            fill: "$success-300",
          },
        },
      },
      danger: {
        fill: "$danger-600",
        "&[data-filled=true]": {
          "&::after": {
            borderWidth: "2px",
            borderColor: "$danger-600",
          },
        },
        "&[data-disabled=true]": {
          backgroundColor: "$danger-50",
          "&::after": {
            borderWidth: "1px",
            borderColor: "$danger-300",
          },
          svg: {
            fill: "$danger-300",
          },
        },
      },
      warning: {
        fill: "$warning-600",
        "&[data-filled=true]": {
          "&::after": {
            borderWidth: "2px",
            borderColor: "$warning-600",
          },
        },
        "&[data-disabled=true]": {
          backgroundColor: "$warning-50",
          "&::after": {
            borderWidth: "1px",
            borderColor: "$warning-300",
          },
          svg: {
            fill: "$warning-300",
          },
        },
      },
      neutral: {
        fill: "$neutral-600",
        "&[data-filled=true]": {
          "&::after": {
            borderWidth: "2px",
            borderColor: "$neutral-600",
          },
        },
        "&[data-disabled=true]": {
          backgroundColor: "$neutral-100",
          "&::after": {
            borderWidth: "1px",
            borderColor: "$neutral-300",
          },
          svg: {
            fill: "$neutral-300",
          },
        },
      },
    },
    size: {
      xs: {
        [`& > ${StyledControl}`]: {
          height: "$11",
          padding: "0 $4",
        },
      },
      sm: {
        [`& > ${StyledControl}`]: {
          height: "$12",
          padding: "0 $4",
        },
      },
      md: {
        [`& > ${StyledControl}`]: {
          height: "$16",
          padding: "0 $4",
        },
      },
    },
  },

  defaultVariants: {
    size: "sm",
  },
});

export const SelectControl = (props: ControlProps & { dataCy?: string }) => {
  const { watch } = useFormContext<{ dataCy?: string }>();
  const {
    size,
    "data-dirty": isDirty,
    variant,
    transparentBg,
  } = props.selectProps as Record<string, any>;

  const { isFieldError } = props.selectProps as Record<string, any>;
  const dataCy = (props.selectProps as any)["data-cy"] || (props.selectProps as any).dataCy || watch("dataCy");

  const isFilled = !isFieldError && props.hasValue;

  return (
    <SelectControlRoot
      data-cy={dataCy}
      size={size}
      data-dirty={isDirty}
      data-open={props.menuIsOpen}
      data-disabled={props.isDisabled}
      data-focused={props.isFocused}
      data-filled={isFilled}
      data-transparent-bg={transparentBg}
      variant={variant}
    >
      <StyledControl {...props} />
    </SelectControlRoot>
  );
};

SelectControl.displayName = "SelectControl";
